import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, FormControl, InputLabel, Select, MenuItem,
  Stack, Typography, IconButton, Box, Avatar,
} from '@mui/material';
import { Close as CloseIcon, PhotoCamera as CameraIcon } from '@mui/icons-material';
import { Membre } from '../types';

interface Props {
  open: boolean;
  membre: Membre | null;
  onClose: () => void;
  onSave: (data: Omit<Membre, 'id'>) => void;
}

const EMPTY = {
  prenom: '', nom: '', nom_jeune_fille: '',
  prenom_naissance: '', prenom_naissance_accord: 'né·e',
  date_naissance: '', date_deces: '',
  lieu_naissance: '', lieu_deces: '',
  sexe: '', statut_marital: '', notes: '', photo: '',
};

const MemberFormDialog: React.FC<Props> = ({ open, membre, onClose, onSave }) => {
  const [form, setForm] = useState<typeof EMPTY>(EMPTY);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isEdit = !!membre;

  useEffect(() => {
    if (open) {
      setForm(membre ? {
        prenom: membre.prenom || '',
        nom: membre.nom || '',
        nom_jeune_fille: membre.nom_jeune_fille || '',
        prenom_naissance: membre.prenom_naissance || '',
        prenom_naissance_accord: membre.prenom_naissance_accord || 'né·e',
        date_naissance: membre.date_naissance ? membre.date_naissance.slice(0, 10) : '',
        date_deces: membre.date_deces ? membre.date_deces.slice(0, 10) : '',
        lieu_naissance: membre.lieu_naissance || '',
        lieu_deces: membre.lieu_deces || '',
        sexe: membre.sexe || '',
        statut_marital: membre.statut_marital || '',
        notes: membre.notes || '',
        photo: membre.photo || '',
      } : { ...EMPTY });
    }
  }, [open, membre]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handlePhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setForm(f => ({ ...f, photo: ev.target?.result as string }));
    reader.readAsDataURL(file);
  };

  const dateError = useMemo(() => {
    if (form.date_naissance && form.date_deces && form.date_deces < form.date_naissance) {
      return 'La date de décès doit être après la date de naissance';
    }
    return '';
  }, [form.date_naissance, form.date_deces]);

  const handleSubmit = () => {
    if (!form.prenom.trim() || !form.nom.trim() || dateError) return;
    const payload: any = { prenom: form.prenom.trim(), nom: form.nom.trim() };
    if (form.nom_jeune_fille.trim()) payload.nom_jeune_fille = form.nom_jeune_fille.trim();
    if (form.prenom_naissance.trim()) {
      payload.prenom_naissance = form.prenom_naissance.trim();
      payload.prenom_naissance_accord = form.prenom_naissance_accord || 'né·e';
    }
    if (form.date_naissance) payload.date_naissance = form.date_naissance;
    if (form.date_deces) payload.date_deces = form.date_deces;
    if (form.lieu_naissance.trim()) payload.lieu_naissance = form.lieu_naissance.trim();
    if (form.lieu_deces.trim()) payload.lieu_deces = form.lieu_deces.trim();
    if (form.sexe) payload.sexe = form.sexe;
    if (form.statut_marital) payload.statut_marital = form.statut_marital;
    if (form.notes.trim()) payload.notes = form.notes.trim();
    payload.photo = form.photo || null;
    onSave(payload);
  };

  const initials = `${form.prenom?.[0] || ''}${form.nom?.[0] || ''}`.toUpperCase();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography variant="h6" fontWeight={700} component="span">
          {isEdit ? 'Modifier le membre' : 'Ajouter un membre'}
        </Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          {/* Photo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={form.photo || undefined}
              sx={{ width: 64, height: 64, fontSize: '1.2rem', fontWeight: 700, bgcolor: 'primary.light' }}
            >
              {!form.photo && initials}
            </Avatar>
            <Box>
              <input type="file" accept="image/*" ref={fileInputRef} style={{ display: 'none' }} onChange={handlePhoto} />
              <Button
                size="small" variant="outlined" startIcon={<CameraIcon fontSize="small" />}
                onClick={() => fileInputRef.current?.click()}
                sx={{ borderRadius: 2 }}
              >
                {form.photo ? 'Changer la photo' : 'Ajouter une photo'}
              </Button>
              {form.photo && (
                <Button size="small" color="error" onClick={() => setForm(f => ({ ...f, photo: '' }))} sx={{ ml: 1 }}>
                  Supprimer
                </Button>
              )}
            </Box>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField label="Prénom *" name="prenom" value={form.prenom} onChange={handleChange} fullWidth size="small" autoFocus />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Nom *" name="nom" value={form.nom} onChange={handleChange} fullWidth size="small" />
            </Grid>
          </Grid>

          <TextField label="Nom de jeune fille" name="nom_jeune_fille" value={form.nom_jeune_fille} onChange={handleChange} fullWidth size="small" />

          <Grid container spacing={2}>
            <Grid item xs={8}>
              <TextField
                label="Prénom de naissance" name="prenom_naissance"
                value={form.prenom_naissance} onChange={handleChange}
                fullWidth size="small"
                placeholder="Prénom d'origine"
                helperText="Si changement de prénom (ex. transition)"
              />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small" disabled={!form.prenom_naissance.trim()}>
                <InputLabel>Accord</InputLabel>
                <Select
                  value={form.prenom_naissance_accord}
                  onChange={e => setForm(f => ({ ...f, prenom_naissance_accord: e.target.value }))}
                  label="Accord"
                >
                  <MenuItem value="né">né</MenuItem>
                  <MenuItem value="née">née</MenuItem>
                  <MenuItem value="né·e">né·e</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField label="Date de naissance" name="date_naissance" type="date" value={form.date_naissance} onChange={handleChange} fullWidth size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Date de décès" name="date_deces" type="date"
                value={form.date_deces} onChange={handleChange}
                fullWidth size="small" InputLabelProps={{ shrink: true }}
                error={!!dateError} helperText={dateError}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField label="Lieu de naissance" name="lieu_naissance" value={form.lieu_naissance} onChange={handleChange} fullWidth size="small" placeholder="Ville, Pays" />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Lieu de décès" name="lieu_deces" value={form.lieu_deces} onChange={handleChange} fullWidth size="small" placeholder="Ville, Pays" />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Sexe</InputLabel>
                <Select value={form.sexe} onChange={e => setForm(f => ({ ...f, sexe: e.target.value }))} label="Sexe">
                  <MenuItem value="">Non renseigné</MenuItem>
                  <MenuItem value="Homme">Homme</MenuItem>
                  <MenuItem value="Femme">Femme</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Statut marital</InputLabel>
                <Select value={form.statut_marital} onChange={e => setForm(f => ({ ...f, statut_marital: e.target.value }))} label="Statut marital">
                  <MenuItem value="">Non renseigné</MenuItem>
                  <MenuItem value="célibataire">Célibataire</MenuItem>
                  <MenuItem value="marié">Marié(e)</MenuItem>
                  <MenuItem value="divorcé">Divorcé(e)</MenuItem>
                  <MenuItem value="veuf">Veuf/Veuve</MenuItem>
                  <MenuItem value="pacsé">Pacsé(e)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <TextField label="Notes" name="notes" value={form.notes} onChange={handleChange} fullWidth size="small" multiline rows={3} placeholder="Informations complémentaires..." />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" size="small">Annuler</Button>
        <Button onClick={handleSubmit} variant="contained" size="small" disabled={!form.prenom.trim() || !form.nom.trim() || !!dateError} disableElevation>
          {isEdit ? 'Enregistrer' : 'Ajouter'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MemberFormDialog;
